import { fetchCsv, TPaymentCsvArgs, TPaymentCsvResult } from '@sim-admin-frontends/data-access';
import {
  getErrorMessage,
  loadingToast,
  toastDismiss,
  TToastType,
  updateToast,
} from '@sim-admin-frontends/ui-shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const TOAST_ID = 'download-csv';

const useDownloadPaymentCsv = () => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [paymentCsvArgs, setPaymentCsvArgs] = useState<TPaymentCsvArgs | null>(null);
  const [blobWithName, setBlobWithName] = useState<TPaymentCsvResult | null>(null);

  const setDownloading = () => {
    setIsDownloading(true);
    document.body.style.cursor = 'wait';
  };

  const resetDownloading = () => {
    setIsDownloading(false);
    document.body.style.cursor = 'default';
  };

  const isSameArgs = (args: TPaymentCsvArgs) => {
    return (
      paymentCsvArgs?.startDate === args.startDate &&
      paymentCsvArgs?.endDate === args.endDate &&
      paymentCsvArgs?.processingStatus === args.processingStatus &&
      paymentCsvArgs?.app === args.app
    );
  };

  const downloadCsv = async (args: TPaymentCsvArgs) => {
    if (isDownloading) {
      return null;
    }

    if (isSameArgs(args)) {
      return blobWithName;
    }

    setDownloading();
    loadingToast(t('common.downloading'), {
      toastId: TOAST_ID,
    });

    let result: TPaymentCsvResult | null = null;
    try {
      result = await fetchCsv(args);
      if (!result) {
        throw new Error('Failed to download CSV');
      }
      setBlobWithName(result);
      setPaymentCsvArgs(args);
    } catch (err) {
      updateToast(TOAST_ID, getErrorMessage(err), TToastType.ERROR);
    }

    resetDownloading();
    toastDismiss(TOAST_ID);
    return result;
  };

  const openCsv = (blob: Blob, filename?: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'payment-export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return { downloadCsv, openCsv, isDownloading };
};

export default useDownloadPaymentCsv;
